import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, Check, AlertTriangle } from "lucide-react";
import { BaseURL } from "../../../../../components/utils/url";
import { useNavigate } from "react-router-dom";

const ChangePassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const parsedData = JSON.parse(userData);
        if (parsedData && parsedData.id) {
          setUserId(parsedData.id);
        }
      }
    } catch (err) {
      console.error("Error retrieving user data from localStorage:", err);
      setError("Could not retrieve user information. Please try again later.");
    }
  }, []);

  const passwordsMatch = newPassword === confirmPassword;
  const isValid = newPassword && confirmPassword && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid) {
      setError("Please ensure your passwords match.");
      return;
    }

    if (step === 1) {
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!userId) {
        setError("User ID not found. Please try logging in again.");
        return;
      }

      setIsLoading(true);

      try {
        const response = await BaseURL.post("/super-admin/reset-password", {
          id: userId,
          newPassword: newPassword,
        });

        if (response.status === 200) {
          setSuccess(true);
          setStep(3);
          // Set a timeout to redirect to logout after showing success message
          setTimeout(() => {
            localStorage.clear(); // Clear localStorage data
            navigate("/administrator/login"); // Redirect to login page
          }, 5000); // 5 seconds delay
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setNewPassword("");
    setConfirmPassword("");
    setStep(1);
    setError("");
    setSuccess(false);
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 my-8">
      <div className="flex items-center justify-center mb-6">
        <Lock className="h-8 w-8 text-gray-700 mr-2" />
        <h2 className="text-2xl font-semibold text-gray-800">
          Change Password
        </h2>
      </div>

      {step === 3 ? (
        <div className="text-center py-8">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-3 rounded-full">
              <Check className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h3 className="text-xl font-medium text-gray-800 mb-4">
            Password Changed Successfully
          </h3>
          <p className="text-gray-600 mb-6">
            Your new password has been set. Please remember to keep it secure.
          </p>
          <div className="bg-gray-100 p-4 rounded-lg mb-6">
            <p className="font-medium text-gray-700">Your password:</p>
            <p className="font-mono bg-gray-200 p-2 mt-2 rounded text-gray-800">
              {newPassword}
            </p>
            <p className="text-sm text-gray-500 mt-3">
              Please make sure you remember this password or store it securely.
            </p>
          </div>
          <p className="text-sm text-blue-600 mb-4">
            You will be redirected to login in a few seconds...
          </p>
          <button
            onClick={() => {
              localStorage.clear();
              navigate("/administrator/login");
            }}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition duration-150"
          >
            Go to Login
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="new-password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
                      placeholder="Enter new password"
                      required
                    />
                    <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirm-password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:outline-none pl-10 ${
                        confirmPassword && !passwordsMatch
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      }`}
                      placeholder="Confirm new password"
                      required
                    />
                    <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <p className="mt-1 text-sm text-red-600">
                      Passwords do not match
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-2">
                Confirm Password Change
              </h3>
              <p className="text-blue-700 mb-4">
                Are you sure you want to change your password?
              </p>
              <div className="bg-white p-3 rounded border border-blue-200 mb-4">
                <p className="text-sm text-gray-500 mb-1">
                  New password will be set to:
                </p>
                <p className="font-mono bg-gray-100 p-2 rounded text-gray-800">
                  {newPassword.replace(/./g, "•")}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-2">
            {step === 2 ? (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`px-6 py-2 text-white rounded-md flex items-center ${
                    isLoading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Confirm Change"
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isValid}
                  className={`px-6 py-2 text-white rounded-md ${
                    isValid
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-blue-300 cursor-not-allowed"
                  }`}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default ChangePassword;
